import ZuiText from '@zenbusiness/zui/Text';

import AnimationProgress from '../AnimationProgress';
import ArticleCard from '../ArticleCard';
import AssistantWelcome from '../AssistantWelcome';
import BusinessCostResults from '../BusinessCostResults';
import BusinessEntityAdvisorResults from '../BusinessEntityAdvisorResults';
import BusinessEntityAdvisorV2Results from '../BusinessEntityAdvisorV2Results';
import BusinessIdeaResults from '../BusinessIdeaResults';
import BusinessNameResults from '../BusinessNameResults';
import ButtonPrompt from '../ButtonPrompt';
import { ArticleCarousel } from '../Carousel';
import ChatErrorMessage from '../ChatErrorMessage';
import CheckIn from '../CheckIn';
import ChoiceFlow from '../ChoiceFlow';
import FeedbackForm from '../FeedbackForm';
import Logo from '../Logo';
import MarketAnalysisReport from '../MarketAnalysisReport';
import Message from '../Message';
import MessageWithCTA from '../MessageWithCTA';
import MessageWithLogo from '../MessageWithLogo';
import MotionDivider from '../MotionDivider';
import ProductCard from '../ProductCard';
import SloganGeneratorResults from '../SloganGeneratorResults';
import StandaloneToolCard from '../StandaloneToolCard';
import SupportCard from '../SupportCard';
import ThinkingAnimation from '../ThinkingAnimation';
import Tracker from '../Tracker';
import UpsellCard from '../UpsellCard';
import UserResponse from '../UserResponse';
import { ComponentTypes } from './constants';

// This is a temporary component to display text messages
// eslint-disable-next-line react/prop-types
function Text({ content }) {
  return <ZuiText>{content}</ZuiText>;
}

export const componentRegistry = Object.freeze({
  [ComponentTypes.ANIMATION_PROGRESS]: AnimationProgress,
  [ComponentTypes.ARTICLE_CARD]: ArticleCard,
  [ComponentTypes.ASSISTANT_WELCOME]: AssistantWelcome,
  [ComponentTypes.BUSINESS_COST_RESULTS]: BusinessCostResults,
  [ComponentTypes.BUSINESS_ENTITY_ADVISOR_RESULTS]: BusinessEntityAdvisorResults,
  [ComponentTypes.BUSINESS_ENTITY_ADVISOR_V2_RESULTS]: BusinessEntityAdvisorV2Results,
  [ComponentTypes.BUSINESS_IDEA_RESULTS]: BusinessIdeaResults,
  [ComponentTypes.BUSINESS_NAME_RESULTS]: BusinessNameResults,
  [ComponentTypes.BUSINESS_RESULTS]: BusinessIdeaResults,
  [ComponentTypes.BUTTON_PROMPT]: ButtonPrompt,
  [ComponentTypes.CAROUSEL]: ArticleCarousel,
  [ComponentTypes.CHAT_ERROR_MESSAGE]: ChatErrorMessage,
  [ComponentTypes.CHECK_IN]: CheckIn,
  [ComponentTypes.CHOICE_FLOW]: ChoiceFlow,
  [ComponentTypes.DIVIDER]: MotionDivider,
  [ComponentTypes.FEEDBACK_FORM]: FeedbackForm,
  [ComponentTypes.LOGO]: Logo,
  [ComponentTypes.MARKET_ANALYSIS_REPORT]: MarketAnalysisReport,
  [ComponentTypes.MESSAGE]: Message,
  [ComponentTypes.MESSAGE_WITH_CTA]: MessageWithCTA,
  [ComponentTypes.MESSAGE_WITH_LOGO]: MessageWithLogo,
  [ComponentTypes.PRODUCT_CARD]: ProductCard,
  [ComponentTypes.SLOGAN_GENERATOR_RESULTS]: SloganGeneratorResults,
  [ComponentTypes.STANDALONE_TOOL_CARD]: StandaloneToolCard,
  [ComponentTypes.SUPPORT_CARD]: SupportCard,
  [ComponentTypes.TEXT]: Text,
  [ComponentTypes.THINKING_ANIMATION]: ThinkingAnimation,
  [ComponentTypes.TRACKER]: Tracker,
  [ComponentTypes.UPSELL_CARD]: UpsellCard,
  [ComponentTypes.USER_RESPONSE]: UserResponse
});

// Helper function to find similar component types
function findSimilarComponentType(type) {
  const availableTypes = Object.keys(componentRegistry);

  // Simple similarity check - could be enhanced with Levenshtein distance
  const similarType = availableTypes.find(
    (availableType) =>
      availableType.toLowerCase().includes(type.toLowerCase()) ||
      type.toLowerCase().includes(availableType.toLowerCase()) ||
      availableType.toLowerCase().replace(/[^a-z]/g, '') ===
        type.toLowerCase().replace(/[^a-z]/g, '')
  );

  return similarType || 'Check available types above';
}

// Enhanced getComponentByType with better error handling
export const getComponentByType = (type, environment = 'development') => {
  const Component = componentRegistry[type];
  if (!Component) {
    if (environment !== 'production') {
      console.warn(
        `[ComponentRegistry] Component type "${type}" not found.`,
        '\n📋 Available types:',
        Object.keys(componentRegistry),
        '\n💡 Did you mean:',
        findSimilarComponentType(type),
        '\n🔍 Total registered components:',
        Object.keys(componentRegistry).length
      );
    }
    return null;
  }

  return Component;
};

// Development utility: Check if a component type is valid
export const isValidComponentType = (type) =>
  Object.prototype.hasOwnProperty.call(componentRegistry, type);

// Development utility: Get registry information for debugging
export const getRegistryInfo = () => ({
  totalComponents: Object.keys(componentRegistry).length,
  availableTypes: Object.keys(componentRegistry).sort(),
  componentNames: Object.keys(componentRegistry).sort(),
  registrySize: Object.keys(componentRegistry).length,
  hasComponent: (type) => isValidComponentType(type),
  findSimilar: (type) => findSimilarComponentType(type)
});

// Development utility: Get component type validation summary
export const getValidationSummary = () => {
  const registryKeys = Object.keys(componentRegistry);
  const typeValues = Object.values(ComponentTypes);

  const missingInRegistry = typeValues.filter((type) => !registryKeys.includes(type));
  const extraInRegistry = registryKeys.filter((key) => !typeValues.includes(key));

  return {
    isValid: missingInRegistry.length === 0 && extraInRegistry.length === 0,
    totalTypes: typeValues.length,
    totalRegistered: registryKeys.length,
    missingFromRegistry: missingInRegistry,
    extraInRegistry,
    coverage: Math.round((registryKeys.length / typeValues.length) * 100)
  };
};

// Development-only validation (runs automatically in development)
if (process.env.NODE_ENV !== 'production') {
  const validateRegistry = () => {
    const validation = getValidationSummary();

    if (!validation.isValid) {
      // eslint-disable-next-line no-console
      console.group('[ComponentRegistry] Validation Issues Found');

      if (validation.missingFromRegistry.length > 0) {
        console.warn('🚨 Missing components in registry:', validation.missingFromRegistry);
      }

      if (validation.extraInRegistry.length > 0) {
        console.warn(
          '⚠️ Extra components in registry (not in ComponentTypes):',
          validation.extraInRegistry
        );
      }
      // eslint-disable-next-line no-console
      console.info(
        `📊 Coverage: ${validation.coverage}% (${validation.totalRegistered}/${validation.totalTypes})`
      );
      // eslint-disable-next-line no-console
      console.groupEnd();
    } else {
      // eslint-disable-next-line no-console
      console.info(
        `[ComponentRegistry] ✅ All ${validation.totalRegistered} components properly registered (${validation.coverage}% coverage)`
      );
    }
  };

  // Run validation automatically in development
  validateRegistry();
}
